import React from "react";
import { Search, BarChart3, MapPin, Globe, Lock } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const SOURCES = [
  { icon: BarChart3, name: "Google Analytics (GA4)", desc: "Sessions, users, traffic sources, and conversions per venue.", metrics: ["Sessions", "Users", "Bounce rate", "Conversions"] },
  { icon: Search, name: "Google Search Console", desc: "Search impressions, clicks, average position, and top queries.", metrics: ["Impressions", "Clicks", "Avg position", "CTR"] },
  { icon: MapPin, name: "Google Business Profile", desc: "Local views, direction requests, calls, and review ratings.", metrics: ["Profile views", "Directions", "Calls", "Rating"] },
];

export default function SEO() {
  return (
    <div className="space-y-6">
      <PageHeader title="SEO & Website Performance" subtitle="Search visibility and site analytics across the group." />

      <Card className="overflow-hidden rounded-[14px] border-[var(--pb-border)] bg-white shadow-[var(--pb-shadow-sm)]">
        <div className="relative">
          <div className="pb-grad-orbs pointer-events-none absolute inset-0" />
          <CardContent className="relative flex flex-col items-start gap-3 p-8">
            <span className="flex h-11 w-11 items-center justify-center rounded-[12px] bg-[rgba(24,52,74,0.08)] text-[#18344A]"><Globe className="h-5 w-5" /></span>
            <h2 className="font-display text-xl font-semibold text-[#18344A]">Connect Google to unlock live insights</h2>
            <p className="max-w-xl text-sm text-[rgba(48,56,60,0.7)]">
              This module is ready for Google Analytics 4, Search Console, and Business Profile. Once connected, traffic and search
              performance will flow into the same shared source of truth as your reservations and revenue.
            </p>
            <Button data-testid="connect-google-button" disabled className="mt-2 gap-2 rounded-full bg-[#18344A] text-white opacity-70">
              <Lock className="h-4 w-4" /> Connect Google (coming soon)
            </Button>
          </CardContent>
        </div>
      </Card>

      <div className="grid gap-6 md:grid-cols-3">
        {SOURCES.map((s) => (
          <Card key={s.name} className="rounded-[14px] border-[var(--pb-border)] bg-white shadow-[var(--pb-shadow-sm)]">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <span className="flex h-9 w-9 items-center justify-center rounded-[10px] bg-[rgba(82,110,126,0.12)] text-[#526E7E]"><s.icon className="h-4 w-4" /></span>
                <Badge variant="outline" className="rounded-full border-[rgba(183,165,142,0.5)] bg-[rgba(183,165,142,0.18)] text-[11px] text-[#7a6a4f]">Not connected</Badge>
              </div>
              <CardTitle className="pt-2 font-display text-base font-semibold text-[#18344A]">{s.name}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-[rgba(48,56,60,0.65)]">{s.desc}</p>
              <div className="mt-4 grid grid-cols-2 gap-2">
                {s.metrics.map((m) => (
                  <div key={m} className="rounded-[10px] border border-dashed border-[var(--pb-border)] bg-[rgba(24,52,74,0.02)] px-3 py-2">
                    <div className="text-[11px] text-[rgba(48,56,60,0.55)]">{m}</div>
                    <div className="font-num text-lg text-[rgba(48,56,60,0.35)]">--</div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
